import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGetData,
  type GetImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGetError,
  type GetImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGetResponse,
  getImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGet,
} from '../../generated';

export type GetImageMixQueryVariables = GetImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGetData['path'];

export type GetImageMixQuery = GetImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGetResponse;

export const useGetImageMixQuery = <
  TData = GetImageMixQuery,
  TError = GetImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGetError,
>(
  { workspace_id, pdp_id }: GetImageMixQueryVariables,
  options?: Omit<UseQueryOptions<GetImageMixQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageMixQuery, TError, TData>({
    queryKey: ['GetImageMix', { workspace_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGet({
        path: { workspace_id, pdp_id },
        throwOnError: true,
      });
      return data as GetImageMixQuery;
    },
    ...options,
  });
};

useGetImageMixQuery.getKey = ({ workspace_id, pdp_id }: GetImageMixQueryVariables) => [
  'GetImageMix',
  { workspace_id, pdp_id },
];

useGetImageMixQuery.fetcher = (
  { workspace_id, pdp_id }: GetImageMixQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getImageMixV1WorkspacesWorkspaceIdPdpsPdpIdImageMixGet({
      path: { workspace_id, pdp_id },
      throwOnError: true,
    });
    return data as GetImageMixQuery;
  };
};

useGetImageMixQuery.schema = {
  method: 'get',
  path: '/v1/workspaces/{workspace_id}/pdps/{pdp_id}/image-mix',
};
